import type { UseIntersectionObserverOptions } from "@/types/hooks";
import { useEffect, useRef } from "react";

/**
 * Hook to observe the intersection of a target element with the viewport
 * @param callback - The callback function to call when the target element intersects the viewport
 * @param options - The options for the intersection observer
 * @returns The ref to the target element
 */
export function useIntersectionObserver(
  callback: (entries: IntersectionObserverEntry[]) => void,
  options: UseIntersectionObserverOptions = {}
) {
  const { threshold = 0.1, rootMargin = "400px" } = options;
  const targetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(callback, {
      threshold,
      rootMargin,
    });

    const currentTarget = targetRef.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [callback, threshold, rootMargin]);

  return targetRef;
}
